/**
 * Data Fetch Context Model
 * Provides context for data fetching strategies
 */

import { FormGroup } from '@angular/forms';
import { IFormField } from './form-field.model';

/**
 * Context interface for data fetching operations
 */
export interface IDataFetchContext {
  /** The field for which data is being fetched */
  field: IFormField;
  
  /** The form group containing all fields */
  formGroup: FormGroup;
  
  /** Master record ID (for edit scenarios) */
  masterId?: number;
  
  /** Master type/form type identifier */
  masterType?: string;
  
  /** Company ID for filtering data */
  companyId: number;
  
  /** Branch ID for filtering data */
  branchId: number;
  
  /** User ID for tracking */
  userId: number;
  
  /** Search term (for searchable fields) */
  searchTerm?: string;
  
  /** All fields in the form (for JSExecutor, Patch, etc.) */
  allFields?: IFormField[];
  
  /** Dependent field value (if this field depends on another) */
  dependentValue?: any;
  
  /** Additional custom context data */
  customData?: Record<string, any>;
}

/**
 * Data fetch context implementation
 */
export class DataFetchContext implements IDataFetchContext {
  constructor(
    public field: IFormField,
    public formGroup: FormGroup,
    public companyId: number,
    public branchId: number,
    public userId: number,
    public masterId?: number,
    public masterType?: string,
    public searchTerm?: string,
    public allFields?: IFormField[],
    public dependentValue?: any,
    public customData?: Record<string, any>
  ) {}
  
  /**
   * Factory method to create context from form state
   */
  static create(
    field: IFormField,
    formGroup: FormGroup,
    globalContext: {
      companyId: number;
      branchId: number;
      userId: number;
      masterId?: number;
      masterType?: string;
    },
    allFields?: IFormField[]
  ): DataFetchContext {
    return new DataFetchContext(
      field,
      formGroup,
      globalContext.companyId,
      globalContext.branchId,
      globalContext.userId,
      globalContext.masterId,
      globalContext.masterType,
      undefined,
      allFields
    );
  }
  
  /**
   * Clone context with new search term
   */
  withSearchTerm(searchTerm: string): DataFetchContext {
    return new DataFetchContext(
      this.field,
      this.formGroup,
      this.companyId,
      this.branchId,
      this.userId,
      this.masterId,
      this.masterType,
      searchTerm,
      this.allFields,
      this.dependentValue,
      this.customData
    );
  }
  
  /**
   * Clone context with dependent value
   */
  withDependentValue(value: any): DataFetchContext {
    return new DataFetchContext(
      this.field,
      this.formGroup,
      this.companyId,
      this.branchId,
      this.userId,
      this.masterId,
      this.masterType,
      this.searchTerm,
      this.allFields,
      value,
      this.customData
    );
  }
  
  /**
   * Clone context with custom data
   */
  withCustomData(data: Record<string, any>): DataFetchContext {
    return new DataFetchContext(
      this.field,
      this.formGroup,
      this.companyId,
      this.branchId,
      this.userId,
      this.masterId,
      this.masterType,
      this.searchTerm,
      this.allFields,
      this.dependentValue,
      { ...this.customData, ...data }
    );
  }
  
  /**
   * Get value of dependent field
   * ✅ Enhanced to support dependentOnKey
   */
  getDependentFieldValue(): any {
    if (!this.field.dependentOn) return null;
    
    // ✅ NEW: Handle dependentOnKey if specified
    if (this.field.dependentOnKey) {
      // Get the selected value from dependentOn field
      const selectedValue = this.formGroup.get(this.field.dependentOn)?.value;
      
      // Find the parent field config to get its data
      const parentField = this.allFields?.find(field => field.dbFeildName === this.field.dependentOn);
      
      if (parentField && parentField.options && selectedValue) {
        // Find the selected item in parent field's data
        const selectedItem = parentField.options.find((item: any) => {
          // Match by patchPropertyName (the value field)
          const patchKey = Object.keys(item).find(key => 
            key.toLowerCase() === parentField.patchPropertyName?.toLowerCase()
          );
          return patchKey && item[patchKey] === selectedValue;
        });
        
        if (selectedItem) {
          // Extract the dependentOnKey value from the selected item
          const dependentKey = Object.keys(selectedItem).find(key => 
            key.toLowerCase() === this.field.dependentOnKey?.toLowerCase()
          );
          return dependentKey ? selectedItem[dependentKey] : selectedValue;
        } else {
          return selectedValue;
        }
      } else {
        // Fallback to getting value directly from dependentOnKey field
        return this.formGroup.get(this.field.dependentOnKey)?.value;
      }
    } else {
      // OLD WAY: No dependentOnKey specified, use dependentOn field value directly
      return this.formGroup.get(this.field.dependentOn)?.value;
    }
  }
  
  /**
   * Get all form values
   */
  getAllFormValues(): Record<string, any> {
    const values: Record<string, any> = {};
    
    this.allFields?.forEach(field => {
      if (field.dbFeildName) {
        values[field.dbFeildName] = this.formGroup.get(field.dbFeildName)?.value;
      }
    });
    
    return values;
  }
  
  /**
   * Check if context has valid dependent value
   */
  hasValidDependentValue(): boolean {
    if (!this.field.dependentOn) return true;
    
    const value = this.getDependentFieldValue();
    return value !== null && value !== undefined && value !== '';
  }
}
